/**
 * Creator Studio — Centralized Defaults
 *
 * This file is the single source of truth for every default value used in the
 * Creator Studio module. Per CS Governance Rule A.3 and Rule F.1:
 *
 *   - Every default value lives here
 *   - Every code path that needs a default imports from this file
 *   - No other file may declare a default value that duplicates one here
 *
 * If you need to add a new default value to the module, add it here first,
 * then import it from the consuming code. Never inline a default literal in
 * feature code.
 *
 * Governance reference: CS Phase 2 Decision Document, Sections A, B, C, D, F.
 */

import { useFontStore } from '@/stores/fontStore';

// ─────────────────────────────────────────────────────────────────────────────
// CANVAS DEFAULTS (CS Rule A.2, F.1.a)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Default canvas width in pixels for new projects.
 * Per CS Rule A.2, settled at 1512×545.
 */
export const DEFAULT_CANVAS_WIDTH = 1512;

/**
 * Default canvas height in pixels for new projects.
 * Per CS Rule A.2, settled at 1512×545.
 */
export const DEFAULT_CANVAS_HEIGHT = 545;

/**
 * Default canvas background color for new projects.
 * White, matching industry-standard design tool conventions.
 * Designers can change per-project; this is the starting state.
 */
export const DEFAULT_BACKGROUND_COLOR = '#ffffff';

// ─────────────────────────────────────────────────────────────────────────────
// TEXT ELEMENT DEFAULTS (CS Rule C.1, D.1, F.1.b)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_TEXT_FONT_SIZE = 24;
export const DEFAULT_TEXT_FONT_WEIGHT = 400;
export const DEFAULT_TEXT_FONT_STYLE: 'normal' | 'italic' = 'normal';

/**
 * Default line height multiplier for text elements.
 * Per CS Rule C.1, this is the single canonical default. Do not introduce
 * additional line-height defaults elsewhere.
 */
export const DEFAULT_TEXT_LINE_HEIGHT = 1.2;

export const DEFAULT_TEXT_LETTER_SPACING = 0;

/**
 * Default text color. Black, matching the new white default background.
 */
export const DEFAULT_TEXT_COLOR = '#000000';

export const DEFAULT_TEXT_ALIGN: 'left' | 'center' | 'right' | 'justify' = 'left';
export const DEFAULT_TEXT_DECORATION: 'none' | 'underline' | 'line-through' = 'none';
export const DEFAULT_TEXT_TRANSFORM: 'none' | 'uppercase' | 'lowercase' | 'capitalize' = 'none';

/**
 * Default text fit mode for newly-created text elements.
 * Per CS Rule D.1, 'natural' means no fit policy is applied — textbox grows
 * and shrinks to content at the design-time font size. This is the absence
 * of a fit mode, retained as the default for backward compatibility.
 */
export const DEFAULT_TEXT_FIT_MODE: 'natural' | 'contain-within-textbox' | 'contain-within-structure' = 'natural';

/**
 * Default minimum font size for text elements that use a fit mode that
 * permits scaling down (contain-within-textbox, contain-within-structure).
 */
export const DEFAULT_TEXT_FONT_SIZE_MIN = 8;

/**
 * Default width intent for text elements.
 * Per CS Rule C.4, 'auto' means width is derived from text content. 'manual'
 * means the designer has explicitly set a width that overrides the content
 * width.
 */
export const DEFAULT_TEXT_WIDTH_INTENT: 'auto' | 'manual' = 'auto';

// ─────────────────────────────────────────────────────────────────────────────
// DEFAULT FONT FAMILY RESOLUTION (CS Rule B.1, F.1.c — Phase 3.2 wired)
// ─────────────────────────────────────────────────────────────────────────────

export function getDefaultTextFont(): {
  fontFamily: string;
  fontVariantId: string;
  fontWeight: number;
  fontStyle: string;
} | null {
  const fonts = useFontStore.getState().fonts;
  const active = fonts.filter((f) => f.isActive);
  if (active.length === 0) return null;

  const sorted = active.slice().sort((a, b) => {
    if (a.fontFamily !== b.fontFamily) {
      return a.fontFamily.localeCompare(b.fontFamily);
    }
    const wa = parseInt(a.fontWeight, 10) || 400;
    const wb = parseInt(b.fontWeight, 10) || 400;
    if (wa !== wb) return wa - wb;
    const sa = a.fontStyle === 'italic' ? 1 : 0;
    const sb = b.fontStyle === 'italic' ? 1 : 0;
    return sa - sb;
  });

  const pick = sorted[0];
  return {
    fontFamily: pick.fontFamily,
    fontVariantId: pick.id,
    fontWeight: parseInt(pick.fontWeight, 10) || 400,
    fontStyle: pick.fontStyle === 'italic' ? 'italic' : 'normal',
  };
}

export function getDefaultTextFontFamily(): string | null {
  const def = getDefaultTextFont();
  return def ? def.fontFamily : null;
}

export function getDefaultButtonFont(): {
  fontFamily: string;
  fontVariantId: string;
  fontWeight: number;
  fontStyle: string;
} | null {
  const fonts = useFontStore.getState().fonts;
  const active = fonts.filter((f) => f.isActive);
  if (active.length === 0) return null;

  const nexaRegular = active.find((f) => {
    if (f.fontFamily !== 'Nexa') return false;
    const w = parseInt(f.fontWeight, 10) || 400;
    if (w !== 400) return false;
    return f.fontStyle !== 'italic';
  });

  if (nexaRegular) {
    return {
      fontFamily: nexaRegular.fontFamily,
      fontVariantId: nexaRegular.id,
      fontWeight: 400,
      fontStyle: 'normal',
    };
  }

  return getDefaultTextFont();
}

// ─────────────────────────────────────────────────────────────────────────────
// BUTTON ELEMENT DEFAULTS (CS Rule F.1.d)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_BUTTON_FONT_SIZE = 16;
export const DEFAULT_BUTTON_FONT_WEIGHT = 600;
export const DEFAULT_BUTTON_BACKGROUND_COLOR = '#06b6d4';
export const DEFAULT_BUTTON_TEXT_COLOR = '#ffffff';
export const DEFAULT_BUTTON_BORDER_RADIUS = 8;
export const DEFAULT_BUTTON_PADDING_X = 24;
export const DEFAULT_BUTTON_PADDING_Y = 12;

// Button font family uses the same resolution function as text.
// Import getDefaultTextFontFamily() above.

// ─────────────────────────────────────────────────────────────────────────────
// SHAPE ELEMENT DEFAULTS (CS Rule F.1.e)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_SHAPE_FILL = '#06b6d4';
export const DEFAULT_SHAPE_STROKE = 'transparent';
export const DEFAULT_SHAPE_STROKE_WIDTH = 0;
export const DEFAULT_SHAPE_BORDER_RADIUS = 0;
export const DEFAULT_SHAPE_FILL_OPACITY = 1;

// ─────────────────────────────────────────────────────────────────────────────
// LINE / PATH ELEMENT DEFAULTS (CS Rule F.1.f)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_LINE_STROKE = '#000000';
export const DEFAULT_LINE_STROKE_WIDTH = 2;
export const DEFAULT_LINE_CAP: 'butt' | 'round' | 'square' = 'round';
export const DEFAULT_LINE_JOIN: 'miter' | 'round' | 'bevel' = 'miter';
export const DEFAULT_LINE_DASH_STYLE: 'solid' | 'dashed' | 'dotted' = 'solid';

// ─────────────────────────────────────────────────────────────────────────────
// ELEMENT-WIDE DEFAULTS (CS Rule F.1.g)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_ELEMENT_OPACITY = 1;
export const DEFAULT_ELEMENT_ROTATION = 0;
export const DEFAULT_ELEMENT_VISIBLE = true;
export const DEFAULT_ELEMENT_LOCKED = false;

/**
 * Default layer assignment for new elements.
 * BACKGROUND_LAYER for most elements. Text and Button elements should be
 * explicitly assigned to OVERLAY_LAYER at creation time by their respective
 * creation paths — do not rely on this default for those types.
 */
export const DEFAULT_ELEMENT_LAYER: 'BACKGROUND_LAYER' | 'OVERLAY_LAYER' = 'BACKGROUND_LAYER';

// ─────────────────────────────────────────────────────────────────────────────
// EDITOR BEHAVIOR DEFAULTS (CS Rule F.1.h)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Auto-save debounce in milliseconds.
 * Auto-save fires this long after the last user modification.
 */
export const DEFAULT_AUTO_SAVE_DEBOUNCE_MS = 3000;

/**
 * Thumbnail regeneration debounce in milliseconds.
 * Thumbnails regenerate this long after the last modification, less
 * frequently than auto-save because thumbnail generation is expensive.
 */
export const DEFAULT_THUMBNAIL_DEBOUNCE_MS = 60000;

/**
 * Auto-save timeout in milliseconds.
 * If an auto-save operation exceeds this duration, it is considered failed.
 */
export const DEFAULT_AUTO_SAVE_TIMEOUT_MS = 15000;

/**
 * Manual save timeout in milliseconds.
 * Manual saves are given more time because they often include thumbnail
 * regeneration.
 */
export const DEFAULT_MANUAL_SAVE_TIMEOUT_MS = 30000;

/**
 * Warn the user when a save payload exceeds this size in kilobytes.
 */
export const DEFAULT_SAVE_PAYLOAD_WARN_KB = 4000;

/**
 * Block the save when a payload exceeds this size in kilobytes.
 */
export const DEFAULT_SAVE_PAYLOAD_BLOCK_KB = 8000;

/**
 * Skip thumbnail generation when a project exceeds this element count.
 * Projects with many elements are too expensive to thumbnail on every save.
 */
export const DEFAULT_THUMBNAIL_SKIP_ELEMENT_THRESHOLD = 50;

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT DEFAULTS (CS Rule F.1.i)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_EXPORT_FORMAT: 'jpeg' | 'png' | 'webp' = 'jpeg';
export const DEFAULT_EXPORT_QUALITY: 'low' | 'medium' | 'high' | 'maximum' = 'high';
export const DEFAULT_EXPORT_SCALE = 1;
export const DEFAULT_EXPORT_INCLUDE_BACKGROUND = true;
export const DEFAULT_EXPORT_INCLUDE_OVERLAY = true;
export const DEFAULT_EXPORT_INCLUDE_COMPOSITE = false;

// ─────────────────────────────────────────────────────────────────────────────
// END OF DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────
